'use client';

import { currentUrl } from '@dealerportal/utilities';
import { useEffect, useRef } from 'react';
import { scrollToElement } from './scrollToElement';

/**
 * The section the URL's anchor names. Read through the analytics gate, which
 * holds `window.location` on the previous route for a moment after a client
 * navigation; a malformed escape (`#%`) names no section.
 */
const anchorOf = (): string | null => {
  const hash = currentUrl().hash.slice(1);
  try {
    return decodeURIComponent(hash);
  } catch {
    return null;
  }
};

/**
 * On arrival with one of `sectionIds` as the URL hash (`#request-form` from
 * an SRP's Order Sample), scroll to that section once it is rendered, clear
 * of the dealer's pinned header. The browser's own jump ignores the header,
 * and misses sections that mount late (the configurator waits for the CiC
 * SDK). Runs once per page.
 */
export const useScrollToHash = (sectionIds: string[]): void => {
  const done = useRef(false);
  // No dependency list: a late section appears on some later render.
  useEffect(() => {
    if (done.current) return;
    const id = anchorOf();
    if (id == null || !sectionIds.includes(id)) {
      done.current = true;
      return;
    }
    if (!document.getElementById(id)) return;
    done.current = true;
    scrollToElement(id);
  });
};
